import { Service } from "vue-typedi";
import dayjs from "dayjs";
import { preparationApiClient } from "@/configuration/api-client";
import { PreparationOrder } from "@/model/preparation/PreparationOrder";
import { PreparationOrderStatus } from "@/model/preparation/PreparationOrderStatus";
import { PreparationOrderHelper } from "@/helpers/PreparationOrderHelper";
import { SerializationHelper } from "@/helpers/SerializationHelper";
import {PreparationOrderProduct} from "@/model/preparation/PreparationOrderProduct";
import {Quantity} from "@/model/Quantity";
import {OrderedProductStatuses} from "@/model/OrderedProductStatus";
import {PartialUpdatePreparationOrderRequest} from "@/model/order/PartialUpdatePreparationOrderRequest";
import { PreparationType } from "@/model/preparation/PreparationType";

const ZONED_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ssZZ";

const TO_PREPARE_STATUSES: PreparationOrderStatus[] = [
    PreparationOrderStatus.TO_PREPARE,
    PreparationOrderStatus.PREPARING,
    PreparationOrderStatus.PREPARED
];

const FINISHED_STATUSES: PreparationOrderStatus[] = [
    PreparationOrderStatus.CANCELED,
    PreparationOrderStatus.DELIVERED,
    PreparationOrderStatus.FAILED
];

@Service()
export class PreparationService {

    private static readonly ORDERS_ENDPOINT: string = '/orders';
    private static readonly STORE_ID: string = "storeId";

    public getOrdersToPrepare(): Promise<PreparationOrder[]> {
        return this.fetchOrders(TO_PREPARE_STATUSES);
    }

    public getFinishedOrders(): Promise<PreparationOrder[]> {
        const maxPickupDate = dayjs().subtract(1, 'day').endOf('day').format(ZONED_DATE_TIME_FORMAT);
        return this.fetchOrders(FINISHED_STATUSES, maxPickupDate);
    }

    public updateOrderStatus(order: PreparationOrder, newStatus: PreparationOrderStatus): Promise<void> {
        const computePreparedQuantity = (p: PreparationOrderProduct) => {
            if (p?.preparedQuantity != null) {
                return p.preparedQuantity;
            }

            if (p.preparationType === PreparationType.ASV) {
                return p.quantity;
            }

            return p.processedQuantity ?? { value: 0, unit: 'piece' } as Quantity;
        }

        const payload: PartialUpdatePreparationOrderRequest = {
            storeId: order.storeId,
            status: newStatus
        };

        if (newStatus === PreparationOrderStatus.PREPARED) {
            const products = order.products
                .filter(p => p.status === OrderedProductStatuses.TO_PREPARE)
                .map(p => ({
                    cug: p.cug,
                    status: OrderedProductStatuses.PREPARED,
                    preparedQuantity: computePreparedQuantity(p)
                }));

            if (products.length > 0) {
                payload.products = products;
            }
        }

        return preparationApiClient.patch(`${PreparationService.ORDERS_ENDPOINT}/${order.orderNumber}`, payload)
            .then(() => undefined);
    }

    public updateProductPreparedQuantity(order: PreparationOrder, product: PreparationOrderProduct, quantity: Quantity): Promise<void> {
        return preparationApiClient.patch(`${PreparationService.ORDERS_ENDPOINT}/${order.orderNumber}`, {
            orderId: product.sourceOrderId,
            storeId: order.storeId,
            products: [{
                cug: product.cug,
                preparedQuantity: { value: quantity.value, unit: quantity.unit }
            }]
        }).then(() => undefined);
    }

    public getExitVoucher(orderNumber: string): Promise<Blob> {
        return preparationApiClient.get(`${PreparationService.ORDERS_ENDPOINT}/${orderNumber}/exit-voucher`, { responseType: 'blob' })
            .then((response: any) => new Blob([response.data], { type: 'application/pdf' }));
    }

    private fetchOrders(orderStatuses: PreparationOrderStatus[], maxPickupDate?: string): Promise<PreparationOrder[]> {
        const params = new URLSearchParams();
        params.append('store', sessionStorage.getItem(PreparationService.STORE_ID));
        orderStatuses.forEach(status => params.append('orderStatuses', status));
        if (maxPickupDate) {
            params.append('maxPickupDate', maxPickupDate);
        }

        return preparationApiClient.get(PreparationService.ORDERS_ENDPOINT, { params })
            .then((response: any) => {
                const data: any[] = response && response.data ? response.data : [];
                const orders = SerializationHelper.toArrayInstanceFromJsonObjArray(() => new PreparationOrder(), data);
                return PreparationOrderHelper.mergeByOrderNumber(orders);
            }).catch(error => Promise.reject(error));
    }
}
